import debug from "debug";

const log = debug("app:eventHub");

/**
 * Tracks UI events like checkbox changes, radio button selections, slider adjustments, and button clicks,
 * and emits corresponding events through the EventHub.
 * @category Utils
 * @subcategory Utils
 */
class UIEventTracker {
  /**
   * Initializes the UIEventTracker instance.
   * @param {Object} experience - The experience object containing the EventHub.
   */
  constructor(experience) {
    this.eventHub = experience.eventHub;
    this.initListeners();
  }

  cleanup() {
    log("Cleaning up UIEventTracker: Removing all event listeners.");

    this.removeAllListeners();
    this.eventHub = null;

    log("UIEventTracker cleanup complete.");
  }

  /**
   * Initializes listeners for various UI components like checkboxes, radio buttons, sliders, and buttons.
   */
  initListeners() {
    // Initialize listeners only once
    this.setupCheckboxListeners([
      "bom-switch",
      "pwr-switch",
      "control-switch",
      "gsg-switch",
      "3d-switch",
      "walls-switch",
      "lids-switch",
      "dark-switch",
      "shadedWithEdges",
    ]);

    this.setupRadioListeners(["mode"]);

    this.setupSliderListeners();

    this.setupButtonListeners();

    this.setupCameraViewListeners();

    this.setupHeaderDarkModeToggle();
    this.setupHeader3DToggle();

    // Custom events
    document.addEventListener("filterForSearch", (event) => {
      this.eventHub.emit("filterForSearch", event.detail);
    });
  }

  /**
   * Sets up listeners for checkboxes and emits events with the checkbox state.
   * @param {string[]} checkboxIds - Array of checkbox element IDs to track.
   */
  setupCheckboxListeners(checkboxIds) {
    checkboxIds.forEach((id) => {
      const checkbox = document.getElementById(id);
      if (checkbox) {
        checkbox.addEventListener("change", (event) => {
          this.eventHub.emit(id, { enabled: event.target.checked });
        });
        // Emit initial state so subscribers get the actual value instead of assuming
        queueMicrotask(() => {
          this.eventHub.emit(id, { enabled: checkbox.checked });
        });
      }
    });
  }

  /**
   * Sets up listeners for radio button groups and emits events when a radio button is selected.
   * @param {string[]} radioGroups - Array of radio button group names to track.
   */
  setupRadioListeners(radioGroups) {
    radioGroups.forEach((name) => {
      const radios = document.querySelectorAll(`input[name='${name}']`);
      radios.forEach((radio) => {
        radio.addEventListener("click", (event) => {
          if (event.target.checked) {
            log(`${name}-change`, event.target.value);
            this.eventHub.emit(`${name}-change`, event.target.value);
          }
        });
      });
    });
  }

  /**
   * Sets up listeners for `.slider-ranges` sliders. Exploded view emits `explodedViewChange` on input.
   * Layer transparency emits `sliderChange` from SliderTransparency only (single layer mapping).
   */
  setupSliderListeners() {
    const sliders = document.querySelectorAll(".slider-ranges");
  
    sliders.forEach((slider) => {
      slider.addEventListener("input", (event) => {
        const id = event.target.id;
        if (id !== "explodedView") return;

        const rawValue = event.target.value;
        const value = 0.99 - rawValue / 100;
        log(`Exploded view slider changed: raw=${rawValue}, mapped=${value}`);
        this.eventHub.emit("explodedViewChange", value);
      });
    });
  }
  
  

  /**
   * Sets up the header 3D mode toggle (2D/3D). Emits "3d-switch" with { enabled } to match existing 3D mode logic.
   */
  setupHeader3DToggle() {
    const toggle = document.getElementById("header-3d-toggle");
    if (!toggle || !this.eventHub) return;

    toggle.addEventListener("click", () => {
      const is3D = toggle.getAttribute("data-mode-3d") === "true";
      const next3D = !is3D;
      toggle.setAttribute("data-mode-3d", String(next3D));
      this.eventHub.emit("3d-switch", { enabled: next3D });
    });
    // Emit initial state so subscribers get the actual value
    queueMicrotask(() => {
      const is3D = toggle.getAttribute("data-mode-3d") === "true";
      this.eventHub.emit("3d-switch", { enabled: is3D });
    });
  }

  /**
   * Sets up the header dark mode toggle (sun/moon). Emits "dark-switch" with { enabled } to match existing dark mode logic.
   */
  setupHeaderDarkModeToggle() {
    const toggle = document.getElementById("header-dark-mode-toggle");
    if (!toggle || !this.eventHub) return;

    toggle.addEventListener("click", () => {
      const isDark = toggle.getAttribute("data-dark") === "true";
      const nextDark = !isDark;
      toggle.setAttribute("data-dark", String(nextDark));
      this.eventHub.emit("dark-switch", { enabled: nextDark });
    });
  }

  /**
   * Sets up listeners for camera view buttons and emits corresponding events when they are clicked.
   */
  setupCameraViewListeners() {
    const topViewButton = document.getElementById("top_view");
    if (topViewButton) {
      topViewButton.addEventListener("mousedown", () => {
        this.eventHub.emit("viewChange", "top");
      });
    }

    const bottomViewButton = document.getElementById("bottom_view");
    if (bottomViewButton) {
      bottomViewButton.addEventListener("mousedown", () => {
        this.eventHub.emit("viewChange", "bottom");
      });
    }
  }

  /**
   * Sets up listeners for various buttons and emits events when they are clicked.
   */
  setupButtonListeners() {
    const buttons = [
      { id: "exportDesign", event: "exportDesign" },
      { id: "sign-in", event: "signInClick" },
      { id: "create", event: "createNewDesignClick" },
      { id: "redo", event: "redo" },
      { id: "undo", event: "undo" },
      { id: "focus", event: "zoomAll" },
      { id: "titleblock", event: "titleblock" },
      { id: "helpmenu", event: "helpmenu" },
      { id: "clear", event: "clearScene" },
      // Import design buttons - emit "importDesign" to trigger Svelte ImportDesignModal
      { id: "importDesign", event: "importDesign" },
      { id: "userImportDesign", event: "importDesign" },
      { id: "guestImportDesign", event: "importDesign" },
      { id: "globalSearch", event: "globalSearch" },
      { id: "copyOption", event: "copyCM" },
      { id: "pasteOption", event: "pasteCM" },
      { id: "deleteOption", event: "deleteCM" },
      { id: "ok", event: "titleblockCapture" },
      { id: "saveAsProject", event: "saveAsProject" },
      { id: "saveProject", event: "saveProject" },
      { id: "saveAsBtn", event: "saveAsBtn" },
      { id: "addToCart", event: "sendToCart" },
      { id: "moreInfo", event: "moreInformation" },
      { id: "configurePartNumber", event: "configurePN" },
      { id: "resetToDefault", event: "resetConfigured" },
    ];

    buttons.forEach((button) => {
      const btn = document.getElementById(button.id);
      if (btn) {
        btn.addEventListener("click", () => {
          log("button clicked", button.id, button.event);
          console.log("button clicked", button.id, button.event);
          this.eventHub.emit(button.event);
        });
      }
    });
  }

  /**
   * Removes all event listeners managed by this instance.
   */
  removeAllListeners() {
    // Logic to remove all event listeners, if necessary
  }
}

export default UIEventTracker;
